import { assign, removeFromArray } from "./common";
import ExtendableObject from "./ExtendableObject";

const EventEmitter = ExtendableObject.extend(
  /** @lends EventEmitter.prototype */
  {
    /**
     * Basic event emitter with added listening functionality
     * @extends ExtendableObject
     * @constructs
     */
    constructor() {
      /**
       * hash of listener records per event
       * @private
       */
      this.listeners = {};

      /**
       * array of records of what this object is listening to
       * @private
       */
      this.listening = [];
    },

    /**
     * Subscribe to an event
     * @param {string} event - name of event
     * @param {function} cb  - callback
     */
    on(event, cb) {
      if(!this.listeners.hasOwnProperty(event)) this.listeners[event] = [];
      this.listeners[event].push(cb);
    },

    /**
     * Cancels subscription to particular event
     * @param {string} event - name of event
     * @param {function} cb  - callback used in {@link EventEmitter#on}
     */
    off(event, cb) {
      if(!this.listeners.hasOwnProperty(event)) return;
      removeFromArray(this.listeners[event], cb);
    },

    /**
     * Invokes all listeners subscribed to `type` event + a special `all` event
     * @param {string} type
     * @param {object} [payload={}]
     * @protected
     */
    emit(type, payload = {}) {
      const evt = assign({ target: this, type }, payload);
      this._emit(type, evt);
      this._emit("all", evt);
    },

    /**
     * Invokes all listeners subscribed to `type` event
     * @private
     * @param {string} event
     * @param {object} payload
     */
    _emit(event, payload) {
      if(!this.listeners.hasOwnProperty(event)) return;
      for(let i = 0; i < this.listeners[event].length; i++) {
        this.listeners[event][i].call(this, payload);
      }
    },

    /**
     * Makes this instance listen to `type` event on `emitter`
     * `"destroy"` event on the `emitter` frees our reference to it,
     * freeing it for the garbage collector
     * @param {EventEmitter} emitter    - emitter to subscribe with
     * @param {string} type             - event name
     * @param {function} cb             - callback
     * @returns {*} - a handle to be used in {@link EventEmitter#stopListeningTo}
     * @protected
     */
    listenTo(emitter, type, cb) {
      const record = { emitter, event: type, cb };
      emitter.on(type, cb);
      this.listening.push(record);
      emitter.on("destroy", () => { removeFromArray(this.listening, record); });
      return record;
    },

    /**
     * Makes this instance stop the listening corresponding to a record returned from {@link EventEmitter#listenTo}
     * @param {*} record
     * @protected
     */
    stopListeningTo(record) {
      const { emitter, event, cb } = record;
      emitter.off(event, cb);
      removeFromArray(this.listening, record);
    },

    /**
     * Stops all listening
     * @protected
     */
    stopListening() {
      this.listening.forEach(({ emitter, event, cb }) => { emitter.off(event, cb); });
      this.listening = [];
    }
  });

/**
 * Utility function for listening to multiple emitters and gathering their results.
 * If one emitter fires more than once before the last one fires, the last result is saved.
 * Only the first parameter to an emitter's callback is saved in the resulting array.
 * @param {EventEmitter[]} emitters - array of the emitters
 * @param {string} event - event name
 * @param {function} cb - callback, which gets invoked with an array of result of emitter_i at index i
 */
EventEmitter.all = function all(emitters, event, cb) {
  let remaining = emitters.length;

  if(remaining === 0) {
    cb([]);
    return;
  }

  const out = new Array(emitters.length);

  function makeCallback(index) {
    return function(evt) {
      if(!out[index]) remaining--;

      out[index] = evt;

      if(!remaining) cb(out);
    }
  }

  for(let i = 0; i < emitters.length; i++) emitters[i].on(event, makeCallback(i));
};

export default EventEmitter;
